import React, { useState } from "react";
import { validateEmail, validatePhone } from "./validationAdmin";
import { toast, Toaster } from "react-hot-toast";

type UserFormData = {
  name: string;
  email: string;
  organization: string;
  phone: string;
  type: "" | "ADVERTISER" | "MERCHANT" | "SIGNAGE";
  role: "" | "ADMIN";
  userType: "" | "BASIC" | "PREMIUM";
};

type PersonalInfoFormProps = {
  userFormData: UserFormData;
  setUserFormData: React.Dispatch<React.SetStateAction<UserFormData>>;
  counter: number;
  setCounter: React.Dispatch<React.SetStateAction<number>>;
};

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
                                                             userFormData,
                                                             setUserFormData,
                                                             counter,
                                                             setCounter,
                                                           }) => {
  const [error, setError] = useState({
    nameError: "",
    emailError: "",
    orgError: "",
    phoneError: "",
    typeError: "",
    roleError: "",
    usertypeError: "",
  });

  const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setUserFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      const error = validateEmail(value);
      setError((prev) => ({ ...prev, emailError: error }));
    }
    if (name === "phone") {
      const error = validatePhone(value);
      setError((prev) => ({ ...prev, phoneError: error }));
    }
  };

  const handleNext = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { name, email, organization, phone, type, role, userType } =
        userFormData;

    if (
        !name.trim() ||
        !email.trim() ||
        !organization.trim() ||
        !phone.trim() ||
        !type ||
        !role ||
        !userType
    ) {
      toast.error("Please fill all the required fields");
      return;
    }

    const emailError = validateEmail(email);
    const phoneError = validatePhone(phone);

    if (emailError || phoneError) {
      setError((prev) => ({
        ...prev,
        emailError,
        phoneError,
      }));
      toast.error("Please correct validation errors before proceeding");
      return;
    }

    // Everything is valid
    console.log("Next clicked, form data:", userFormData);
    setCounter(counter + 1);
  };

  return (
      <>
        <Toaster position="top-right" reverseOrder={false} />
        <form
            className="relative grid grid-cols-1 gap-6 pb-16 mx-auto personal-info-form-container md:grid-cols-2"
            onSubmit={handleNext}
        >
          {/* Name */}
          <div className="flex flex-col">
            <label htmlFor="name" className="mb-1 text-lg">
              Name <span className="text-red-500">*</span>
            </label>
            <input
                id="name"
                name="name"
                value={userFormData.name}
                onChange={handleChange}
                type="text"
                className="w-full p-4 border rounded-xl border-neutral-300"
                placeholder="Enter your name"
            />
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label htmlFor="email" className="mb-1 text-lg">
              Email <span className="text-red-500">*</span>
            </label>
            <input
                id="email"
                name="email"
                value={userFormData.email}
                onChange={handleChange}
                type="email"
                className="w-full p-4 border rounded-xl border-neutral-300"
                placeholder="Enter your email"
            />
            {
                error.emailError && <span className="ml-2 text-sm text-red-500">{error.emailError}</span>
            }
          </div>

          {/* Organization */}
          <div className="flex flex-col">
            <label htmlFor="organization" className="mb-1 text-lg">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input
                id="organization"
                name="organization"
                value={userFormData.organization}
                onChange={handleChange}
                type="text"
                className="w-full p-4 border rounded-xl border-neutral-300"
                placeholder="Enter organization"
            />
          </div>

          {/* Phone */}
          <div className="flex flex-col">
            <label htmlFor="phone" className="mb-1 text-lg">
              Phone No <span className="text-red-500">*</span>
            </label>
            <input
                id="phone"
                name="phone"
                value={userFormData.phone}
                onChange={handleChange}
                type="tel"
                inputMode="numeric"
                className="w-full p-4 border rounded-xl border-neutral-300"
                placeholder="Enter phone number"
                pattern="\d*"
            />
            {
                error.phoneError && <span className="ml-2 text-sm text-red-500">{error.phoneError}</span>
            }
          </div>

          {/* Type Dropdown */}
          <div className="flex flex-col">
            <label htmlFor="type" className="mb-1 text-lg">
              Type <span className="text-red-500">*</span>
            </label>
            <select
                id="type"
                name="type"
                value={userFormData.type}
                onChange={handleChange}
                className="w-full p-4 border rounded-xl border-neutral-300"
            >
              <option value="" disabled>
                Select Type
              </option>
              <option value="ADVERTISER">Advertiser</option>
              <option value="MERCHANT">Merchant</option>
              <option value="SIGNAGE">Signage</option>
            </select>
          </div>

          {/* Role Dropdown */}
          <div className="flex flex-col">
            <label htmlFor="role" className="mb-1 text-lg">
              Role <span className="text-red-500">*</span>
            </label>
            <select
                id="role"
                name="role"
                value={userFormData.role}
                onChange={handleChange}
                className="w-full p-4 border rounded-xl border-neutral-300"
            >
              <option value="" disabled>
                Select Role
              </option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>

          {/* User Type Dropdown */}
          <div className="flex flex-col">
            <label htmlFor="userType" className="mb-1 text-lg">
              User Type <span className="text-red-500">*</span>
            </label>
            <select
                id="userType"
                name="userType"
                value={userFormData.userType}
                onChange={handleChange}
                className="w-full p-4 border rounded-xl border-neutral-300"
            >
              <option value="" disabled>
                Select User Type
              </option>
              <option value="BASIC">Basic</option>
              <option value="PREMIUM">Premium</option>
            </select>
          </div>

          {/* Next Button */}
          <div className="absolute bottom-0 right-0 mt-4">
            <button
                type="submit"
                className="px-3 py-3 font-semibold text-white transition rounded bg-violet-600 hover:bg-violet-700"
            >
              Next
            </button>
          </div>
        </form>
      </>
  );
};

export default PersonalInfoForm;